import React, { useEffect, useMemo, useState } from "react";
import { buildApiUrl } from "../config/api";
import "./AdminNewsletter.css";

function formatDate(value) {
  if (!value) return "—";

  try {
    return new Date(value).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function cleanText(value) {
  if (value === null || value === undefined || value === "") return "—";
  return value;
}

function BreakdownCard({ title, items, labelKey, valueKey }) {
  return (
    <div className="admin-newsletter-breakdown-card">
      <h3>{title}</h3>

      {items && items.length > 0 ? (
        <div className="admin-newsletter-breakdown-list">
          {items.map((item, index) => (
            <div className="admin-newsletter-breakdown-row" key={`${title}-${index}`}>
              <span>{cleanText(item[labelKey])}</span>
              <strong>{cleanText(item[valueKey])}</strong>
            </div>
          ))}
        </div>
      ) : (
        <p className="admin-newsletter-empty-small">No data available.</p>
      )}
    </div>
  );
}

function AdminNewsletter() {
  const [stats, setStats] = useState(null);
  const [subscribers, setSubscribers] = useState([]);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [statsLoading, setStatsLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  const summaryCards = useMemo(() => {
    const summary = stats?.summary || {};

    return [
      {
        label: "Total Subscribers",
        value: summary.total_subscribers || 0,
      },
      {
        label: "Active Subscribers",
        value: summary.active_subscribers || 0,
      },
      {
        label: "Unsubscribed",
        value: summary.unsubscribed_subscribers || 0,
      },
      {
        label: "Today",
        value: summary.today_subscribers || 0,
      },
    ];
  }, [stats]);

  useEffect(() => {
    let ignore = false;

    async function loadStats() {
      try {
        setStatsLoading(true);
        setError("");

        const response = await fetch(buildApiUrl("/admin/newsletter/stats"), {
          credentials: "include",
        });
        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(data.message || "Failed to load newsletter stats.");
        }

        if (!ignore) {
          setStats(data);
        }
      } catch (err) {
        if (!ignore) {
          setError(err.message || "Failed to load newsletter stats.");
        }
      } finally {
        if (!ignore) {
          setStatsLoading(false);
        }
      }
    }

    loadStats();

    return () => {
      ignore = true;
    };
  }, [refreshKey]);

  useEffect(() => {
    const controller = new AbortController();
    let ignore = false;

    async function loadSubscribers() {
      try {
        setTableLoading(true);
        setError("");

        const params = new URLSearchParams();
        params.set("page", String(page));
        params.set("limit", String(limit));

        if (statusFilter) {
          params.set("status", statusFilter);
        }

        if (search.trim()) {
          params.set("search", search.trim());
        }

        const response = await fetch(
          buildApiUrl(`/admin/newsletter/subscribers?${params.toString()}`),
          { signal: controller.signal, credentials: "include" }
        );

        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(data.message || "Failed to load subscribers.");
        }

        if (!ignore) {
          setSubscribers(data.subscribers || []);
          setTotal(data.total || 0);
          setTotalPages(data.totalPages || 1);
        }
      } catch (err) {
        if (err.name !== "AbortError" && !ignore) {
          setError(err.message || "Failed to load subscribers.");
        }
      } finally {
        if (!ignore) {
          setTableLoading(false);
        }
      }
    }

    loadSubscribers();

    return () => {
      ignore = true;
      controller.abort();
    };
  }, [page, limit, statusFilter, search, refreshKey]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const handleClearFilters = () => {
    setSearchInput("");
    setSearch("");
    setStatusFilter("");
    setPage(1);
    setLimit(25);
  };

  const handleExportCsv = () => {
    window.open(buildApiUrl("/admin/newsletter/export"), "_blank");
  };

  const handleStatusToggle = async (subscriber) => {
    try {
      setActionMessage("");

      const nextStatus =
        subscriber.status === "active" ? "unsubscribed" : "active";

      const response = await fetch(
        buildApiUrl(`/admin/newsletter/subscribers/${subscriber.id}/status`),
        {
          method: "PATCH",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status: nextStatus }),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || "Failed to update subscriber status.");
      }

      setActionMessage(data.message || "Subscriber status updated.");
      setRefreshKey((current) => current + 1);
    } catch (err) {
      setError(err.message || "Failed to update subscriber status.");
    }
  };

  return (
    <main className="admin-newsletter-page">
      <section className="admin-newsletter-hero">
        <div>
          <p className="admin-newsletter-kicker">MayaChitro Admin</p>
          <h1>Newsletter Subscribers</h1>
          <p>
            Track subscriber growth, signup source, device data, and export your
            audience for future campaigns.
          </p>
        </div>

        <button className="admin-newsletter-export-btn" onClick={handleExportCsv}>
          Export CSV
        </button>
      </section>

      {error && <div className="admin-newsletter-alert error">{error}</div>}
      {actionMessage && (
        <div className="admin-newsletter-alert success">{actionMessage}</div>
      )}

      <section className="admin-newsletter-stats-grid">
        {summaryCards.map((card) => (
          <div className="admin-newsletter-stat-card" key={card.label}>
            <span>{card.label}</span>
            <strong>{statsLoading ? "..." : card.value}</strong>
          </div>
        ))}
      </section>

      <section className="admin-newsletter-breakdown-grid">
        <BreakdownCard
          title="Subscribers by Country"
          items={stats?.byCountry || []}
          labelKey="country"
          valueKey="total"
        />

        <BreakdownCard
          title="Subscribers by Source"
          items={stats?.bySource || []}
          labelKey="source"
          valueKey="total"
        />

        <BreakdownCard
          title="Subscribers by Device"
          items={stats?.byDevice || []}
          labelKey="device_type"
          valueKey="total"
        />
      </section>

      <section className="admin-newsletter-table-card">
        <div className="admin-newsletter-table-header">
          <div>
            <p className="admin-newsletter-kicker">Subscriber List</p>
            <h2>Recent Subscribers</h2>
            <p>{total} total record{total === 1 ? "" : "s"}</p>
          </div>

          <form className="admin-newsletter-filters" onSubmit={handleSearchSubmit}>
            <input
              type="text"
              placeholder="Search email, country, city, source..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />

            <select
              value={statusFilter}
              onChange={(e) => {
                setPage(1);
                setStatusFilter(e.target.value);
              }}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="unsubscribed">Unsubscribed</option>
            </select>

            <select
              value={limit}
              onChange={(e) => {
                setPage(1);
                setLimit(Number(e.target.value));
              }}
            >
              <option value={10}>10 rows</option>
              <option value={25}>25 rows</option>
              <option value={50}>50 rows</option>
              <option value={100}>100 rows</option>
            </select>

            <button type="submit">Search</button>
            <button type="button" className="ghost" onClick={handleClearFilters}>
              Clear
            </button>
          </form>
        </div>

        <div className="admin-newsletter-table-wrap">
          <table className="admin-newsletter-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Status</th>
                <th>Source</th>
                <th>Location</th>
                <th>Device</th>
                <th>Signup Page</th>
                <th>Created</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {tableLoading ? (
                <tr>
                  <td colSpan="8" className="admin-newsletter-table-message">
                    Loading subscribers...
                  </td>
                </tr>
              ) : subscribers.length === 0 ? (
                <tr>
                  <td colSpan="8" className="admin-newsletter-table-message">
                    No subscribers found.
                  </td>
                </tr>
              ) : (
                subscribers.map((subscriber) => (
                  <tr key={subscriber.id}>
                    <td>
                      <strong>{subscriber.email}</strong>
                      <span>{cleanText(subscriber.language)}</span>
                    </td>

                    <td>
                      <span
                        className={`admin-newsletter-status ${subscriber.status}`}
                      >
                        {subscriber.status}
                      </span>
                    </td>

                    <td>
                      <strong>{cleanText(subscriber.source)}</strong>
                      <span>{cleanText(subscriber.utm_source)}</span>
                    </td>

                    <td>
                      <strong>
                        {subscriber.city || subscriber.region || subscriber.country
                          ? `${subscriber.city || ""}${
                              subscriber.city && subscriber.country ? ", " : ""
                            }${subscriber.country || ""}`
                          : "Unknown"}
                      </strong>
                      <span>{cleanText(subscriber.timezone)}</span>
                    </td>

                    <td>
                      <strong>{cleanText(subscriber.device_type)}</strong>
                      <span>
                        {cleanText(subscriber.browser_name)} /{" "}
                        {cleanText(subscriber.os_name)}
                      </span>
                    </td>

                    <td>
                      <strong>{cleanText(subscriber.signup_page)}</strong>
                      <span>{cleanText(subscriber.screen_size)}</span>
                    </td>

                    <td>
                      <strong>{formatDate(subscriber.created_at)}</strong>
                      <span>Updated: {formatDate(subscriber.updated_at)}</span>
                    </td>

                    <td>
                      <button
                        className="admin-newsletter-row-btn"
                        onClick={() => handleStatusToggle(subscriber)}
                      >
                        {subscriber.status === "active"
                          ? "Unsubscribe"
                          : "Reactivate"}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="admin-newsletter-pagination">
          <button
            onClick={() => setPage((current) => Math.max(current - 1, 1))}
            disabled={page <= 1}
          >
            Previous
          </button>

          <span>
            Page {page} of {totalPages}
          </span>

          <button
            onClick={() =>
              setPage((current) => Math.min(current + 1, totalPages))
            }
            disabled={page >= totalPages}
          >
            Next
          </button>
        </div>
      </section>
    </main>
  );
}

export default AdminNewsletter;
